import { captureException } from "@sentry/react"
import {
  ErrorComponentProps,
  useLocation,
  useMatches,
  useMatchRoute,
} from "@tanstack/react-router"
import { WelcomeFailReason } from "ipctg-inflow-types/InFlowEnums"
import { useTranslation } from "react-i18next"

import { ExpectedError } from "@/utilities/expectedErrors"
import { FetchError } from "@/utilities/fetch++"

import { LinkButton } from "../atoms/Button"
import Card from "../atoms/Card"
import Heading from "../atoms/Heading"
import Icon from "../atoms/Icon"
import Text from "../atoms/Text"

interface Props extends ErrorComponentProps {
  resolution: "goHomeHome" | "restartFromWelcome" | "restartViaPAM"
}

function ErrorComponent(props: Props) {
  const matchRoute = useMatchRoute()
  const matches = useMatches()
  const params = matchRoute({ to: "/qr/$qrLookupID", fuzzy: true })
  const location = useLocation()
  const { error } = props
  const { t } = useTranslation()

  const isOnRootQR = matches.some((match) => match.id === "qr/qrLookupID")

  const qrLookupID = (params && params.qrLookupID) || location.search.qrLookupID

  return (
    <div className="error-boundary">
      <Card>
        <Icon size="x-large" name="warning" />
        <div>
          <Heading level={2} as={1} className="center">
            {heading() || t("errors.common.error")}
          </Heading>

          <Text className="center">{message()}</Text>
        </div>

        {!shouldGoHomeHome() && qrLookupID ? (
          <Resolution resolution="restartFromWelcome" qrLookupID={qrLookupID} />
        ) : (
          <Resolution
            resolution={
              props.resolution === "restartFromWelcome"
                ? "goHomeHome"
                : props.resolution
            }
          />
        )}
        <Text className="center" level={2}>
          {t("common.getHelp")}{" "}
          {!qrLookupID || shouldGoHomeHome() ? (
            <LinkButton
              hue="primary"
              presentation="link"
              to="/help"
              style={{ textDecoration: "underline" }}
            >
              {t("common.clickHere")}
            </LinkButton>
          ) : (
            <LinkButton
              hue="primary"
              presentation="link"
              to="/qr/$qrLookupID/help"
              params={{ qrLookupID }}
              style={{ textDecoration: "underline" }}
            >
              {t("common.clickHere")}
            </LinkButton>
          )}
        </Text>
      </Card>
    </div>
  )

  function shouldGoHomeHome() {
    const qrErrors = [
      WelcomeFailReason.QRNotFound,
      WelcomeFailReason.QRNotActive,
    ]
    return (
      (error instanceof ExpectedError &&
        qrErrors.some((r) => r === error.failReason)) ||
      (!isOnRootQR && !qrLookupID)
    )
  }

  function heading() {
    if (error instanceof ExpectedError) {
      return
    } else {
      return error.name
    }
  }

  function message() {
    if (error instanceof ExpectedError) {
      return t(error.translationKey)
    } else if (error instanceof FetchError) {
      switch (error.response.status) {
        case 401:
          return t("errors.common.unauthorized")
        case 403:
          return t("errors.common.unauthorized")
        case 404:
          return t("errors.common.notFound")
      }
    }

    captureException(error)
    return error.message
  }
}

type ResolutionProps =
  | {
      resolution: Extract<Props["resolution"], "restartFromWelcome">
      qrLookupID: string
    }
  | {
      resolution: Exclude<Props["resolution"], "restartFromWelcome">
    }

function Resolution(props: ResolutionProps) {
  const { t } = useTranslation()
  switch (props.resolution) {
    case "goHomeHome":
      return (
        <LinkButton hue="primary" variant="default" to="/">
          {t("common.goHome")}
        </LinkButton>
      )
    case "restartFromWelcome":
      return (
        <LinkButton
          hue="secondary"
          variant="default"
          to="/qr/$qrLookupID"
          params={{ qrLookupID: props.qrLookupID }}
        >
          {t("common.goHome")}
        </LinkButton>
      )
    case "restartViaPAM":
      return (
        <Text className="center">
          {t("errors.errorBoundary.resolution.rescanPAM")}
        </Text>
      )
  }
}

export default ErrorComponent
